import React, { useState, useRef, useEffect } from 'react';
import PopupMenu from '~/editor/popupMenu/PopupMenu';
import { SequenceIcon } from '~/training/navigation/SequenceIcon';
import { Capsule } from '~/ui/capsules';
import { useEditorStore, EditorChapter, EditorTraining } from '~/shell/Store';
import { useMoveStore } from '~/editor/store/moveElementStore';
import { resolveSequenceDropIndex } from '~/editor/lib/sequencePositions';
import styled from 'styled-components';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import DotsIcon from '~/assets/button/dotsVertical.svg';

const DotsIconForBtn = styled(DotsIcon)`
  cursor: pointer;
  margin-top: 5px;

  position: absolute;
  right: 5px;
  width: 16px;
  padding-left: 4px;
`;

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move;
`;

const SequenceTitle = styled.div`
  display: flex;
  align-items: center;

  span {
    background-color: #f5f5f5;
    svg {
      path {
        fill: #000 !important;
      }
    }
  }

  p {
    line-height: 16px;
    padding-left: 8px;
  }

  textarea {
    resize: none;
    width: 126px;
    background: transparent;
    border: none;
    outline: 2px solid #9845fb;
    border-radius: 0px;
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 4px;
  }
`;

export const SequenceItem = ({
  data,
  selectItem,
  index,
  moveItem,
  chapterId,
  chapterSequenceId,
  setDraggable,
  draggable,
  mode
}: {
  data: EditorChapter['chapterSequences'][number];
  selectItem: (seq: { id: number }, training?: EditorTraining) => void;
  index: number;
  moveItem: (
    chapterId: number | string,
    chapterSequenceId: number | string,
    hoverIndex: number
  ) => void;
  chapterId: number | string;
  chapterSequenceId: number | string;
  setDraggable: React.Dispatch<React.SetStateAction<boolean>>;
  draggable: boolean;
  mode?: 'editor' | 'preview' | 'translation';
}) => {
  const {
    sequence,
    training,
    setTraining,
    apiService,
    translationScope,
    markContentEdited
  } = useEditorStore();
  const [isDragOver, setIsDragOver] = useState(false);
  const [classNameForDragOver, setClassNameForDragOver] = useState<string>('');

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState(data?.sequence?.title as string | undefined);
  // Captures the title at the moment rename starts. `data.sequence.title` is
  // updated live on every keystroke (see the `[text]` effect below), so it can't
  // be used to tell whether the title actually changed on blur.
  const renameStartTitleRef = useRef(
    data?.sequence?.title as string | undefined
  );
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isRename, setIsRename] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setText(data?.sequence?.title as string | undefined);
  }, [data]);

  useEffect(() => {
    if (text!.trim() !== '') {
      const updatedChapters = training!.chapters.map(
        (chapter: EditorChapter) => {
          return {
            ...chapter,
            chapterSequences: chapter.chapterSequences.map(
              (chapterSequence: EditorChapter['chapterSequences'][number]) => {
                return chapterSequence.id === data.id
                  ? {
                      ...chapterSequence,
                      sequence: { ...chapterSequence.sequence, title: text }
                    }
                  : chapterSequence;
              }
            )
          };
        }
      );
      setTraining({ ...training!, chapters: updatedChapters });
    }
  }, [text]);

  const handleButtonClick = (event: React.MouseEvent<SVGElement>) => {
    setAnchorElement(event.currentTarget as unknown as HTMLElement);
    setIsPopupOpen(true);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    useMoveStore.getState().startSequenceMove(chapterSequenceId);
    e.dataTransfer.setData(
      'sequence',
      JSON.stringify({ index, chapterId, chapterSequenceId })
    );

    if (window) {
      window.sessionStorage.setItem('dragItemId', chapterSequenceId as string);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);

    if (window) {
      const dragItemId = window.sessionStorage.getItem('dragItemId');
      const currentId = e.currentTarget.dataset.id;

      const allSequences = training!.chapters.flatMap(
        (chapter: EditorChapter) => chapter.chapterSequences
      );
      const dragIndex = allSequences.findIndex(
        (item: EditorChapter['chapterSequences'][number]) =>
          (item.id as unknown) == dragItemId
      );
      const dropIndex = allSequences.findIndex(
        (item: EditorChapter['chapterSequences'][number]) =>
          item.id == currentId
      );

      if (dragIndex === dropIndex) {
        setClassNameForDragOver('');
      } else {
        setClassNameForDragOver(
          dropIndex > dragIndex ? 'drag-over-bottom' : 'drag-over-top'
        );
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragOver(false);
    const data = e.dataTransfer.getData('sequence');

    if (!data) {
      return;
    }

    try {
      const draggedData = JSON.parse(data);

      if (draggedData.index == index && draggedData.chapterId == chapterId) {
        return;
      }

      moveItem(
        chapterId,
        draggedData.chapterSequenceId,
        resolveSequenceDropIndex({
          chapters: training!.chapters,
          draggedChapterSequenceId: draggedData.chapterSequenceId,
          draggedChapterId: draggedData.chapterId,
          dropTargetChapterSequenceId: chapterSequenceId,
          dropTargetIndex: index,
          dropTargetChapterId: chapterId
        })
      );

      setTimeout(() => {
        useMoveStore.getState().stopSequenceMove();
      }, 500);
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };

  const handleBlur = () => {
    setDraggable(true);
    if (apiService && data && data.sequence && data.sequence.id) {
      if (text!.trim() === '') {
        setText(data.sequence.title as string | undefined);
        setIsRename(false);
        return;
      }
      apiService.updateSequence(data.sequence.id, { title: text });
      // Renaming a sequence is a content edit — enable the Publish button.
      // Compare against the title captured when rename started, because
      // `data.sequence.title` is updated live while typing.
      if (text !== renameStartTitleRef.current) {
        markContentEdited();
      }
    }
    setIsRename(false);
  };

  useEffect(() => {
    setIsPopupOpen(false);

    if (isRename && inputRef.current) {
      inputRef.current.focus();
      // Remember the title before the user starts editing it.
      renameStartTitleRef.current = text;
    }
  }, [isRename]);

  const dataSequenceId = data?.sequence?.id;
  const isActive =
    !!dataSequenceId &&
    (mode === 'translation'
      ? translationScope.kind === 'sequence' &&
        translationScope.sequenceId === dataSequenceId
      : sequence?.id === dataSequenceId);

  return (
    <div
      className={`sequenceItem ${isDragOver ? classNameForDragOver : ''} ${
        isActive ? 'active' : ''
      }`}
      data-id={chapterSequenceId}
      data-index={index}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DragAndDropIconForBtn />
      <SequenceTitle
        onMouseUp={() => (!isRename ? selectItem(data?.sequence) : false)}
      >
        <Capsule>
          <SequenceIcon icon={data?.sequence?.icon as string | undefined} />
        </Capsule>
        {!isRename ? (
          <p>{text}</p>
        ) : (
          <textarea
            ref={inputRef}
            value={text}
            rows={2}
            style={{
              resize: 'none',
              width: '126px',
              background: 'transparent',
              border: 'none'
            }}
            onFocus={() => setDraggable(false)}
            onChange={e => setText(e.target.value)}
            onBlur={handleBlur}
          />
        )}
      </SequenceTitle>
      <DotsIconForBtn onClick={handleButtonClick} />
      <PopupMenu
        id={data?.sequence?.id as unknown as string | undefined}
        setIsRename={setIsRename}
        isOpen={isPopupOpen}
        anchorElement={anchorElement}
        onClose={() => setIsPopupOpen(false)}
        data={data}
        selectItem={selectItem}
      />
    </div>
  );
};
